/**
 * BusinessTripApprovals — Wave 3.2 live-API wrapper around <ApprovalList>.
 *
 * Backend data shape for type='business_trip' (from seed_test_data.py):
 *   { destination, start_date, end_date, purpose }
 *
 * Mock also had `days`; derive it from start/end dates when absent so
 * the column stays useful regardless of which shape the payload arrives in.
 */

import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import ApprovalList, { ApprovalColumn, ApprovalFormField } from './ApprovalList';
import { useTranslation } from 'react-i18next';

function pickStr(data: unknown, key: string): string {
  return typeof data === 'object' && data !== null && key in data
    ? String((data as Record<string, unknown>)[key] ?? '')
    : '';
}

function pickNum(data: unknown, key: string): number | null {
  if (typeof data !== 'object' || data === null || !(key in data)) return null;
  const v = (data as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : null;
}

function inclusiveDays(startIso: string, endIso: string): number | null {
  if (!startIso || !endIso) return null;
  const s = Date.parse(startIso + 'T00:00:00Z');
  const e = Date.parse(endIso + 'T00:00:00Z');
  if (!Number.isFinite(s) || !Number.isFinite(e) || e < s) return null;
  return Math.round((e - s) / 86_400_000) + 1;
}

const BusinessTripApprovals: React.FC = () => {
  const { t } = useTranslation();

  const columns = useMemo<ApprovalColumn[]>(() => [
    {
      label: t('attendance:approvals.businessTrip.colDestination'),
      render: (r) => pickStr(r.data, 'destination') || '—',
    },
    {
      label: t('attendance:approvals.businessTrip.colPeriod'),
      render: (r) => (
        <Typography variant="caption">
          {pickStr(r.data, 'start_date') || '—'} →{' '}
          {pickStr(r.data, 'end_date') || '—'}
        </Typography>
      ),
    },
    {
      label: t('attendance:approvals.businessTrip.colDays'),
      align: 'center',
      render: (r) => {
        // The server writes both counts since 2026-08-25. `inclusiveDays` stays as the
        // fallback for rows written before that, so this column does not blank out on
        // history. The working count appears only when it differs from the duration —
        // which is exactly when it is worth reading.
        const explicit = pickNum(r.data, 'days');
        const days = explicit !== null ? explicit : inclusiveDays(
          pickStr(r.data, 'start_date'),
          pickStr(r.data, 'end_date'),
        );
        if (days === null) return '—';
        const working = pickNum(r.data, 'working_days');
        return working !== null && working !== days
          ? t('attendance:approvals.businessTrip.daysWithWorking', { days, working })
          : days;
      },
    },
    {
      label: t('attendance:approvals.businessTrip.colPurpose'),
      render: (r) => (
        <Typography variant="caption" color="text.secondary">
          {pickStr(r.data, 'reason') || '—'}
        </Typography>
      ),
    },
  ], [t]);

  const createFields = useMemo<ApprovalFormField[]>(() => [
    // `place`, not `text`: mobile and the worker portal have both offered address
    // autocomplete on this exact field, and this surface made the admin type it.
    { key: 'destination', label: t('attendance:approvals.businessTrip.colDestination'), type: 'place', required: true, span: 6 },
    {
      // The DURATION — how long the person is away. An inclusive subtraction of the
      // two dates, which is exactly what the server stores in `days`, so the number
      // here and the stored one agree.
      key: 'days',
      label: t('attendance:approvals.businessTrip.colDays'),
      type: 'derived_days',
      span: 6,
      derivedFromDates: ['start_date', 'end_date'],
    },
    {
      // The WORKING-day count, from the server for THIS worker. Not computable here:
      // rest days live on the org's AttendanceRule and holidays on a per-worker
      // calendar. Read-only and never submitted — `create_approval` writes it.
      key: 'working_days',
      label: t('attendance:approvals.businessTrip.formWorkingDays'),
      type: 'derived_working_days',
      span: 6,
      derivedFromDates: ['start_date', 'end_date'],
      placeholder: t('attendance:approvals.helperWorkingDays'),
    },
    { key: 'start_date', label: t('attendance:approvals.businessTrip.formStartDate'), type: 'date', required: true, span: 6 },
    { key: 'end_date', label: t('attendance:approvals.businessTrip.formEndDate'), type: 'date', required: true, span: 6 },
    { key: 'reason', label: t('attendance:approvals.businessTrip.colPurpose'), type: 'textarea', required: true },
  ], [t]);

  return (
    <ApprovalList
      type="business_trip"
      title={t('attendance:approvals.businessTripTitle')}
      description={t('attendance:approvals.businessTripDescription')}
      recordTitle={t('attendance:approvals.businessTrip.recordTitle')}
      filterLabel={t('attendance:approvals.filterLabel')}
      columns={columns}
      createFields={createFields}
    />
  );
};

export default BusinessTripApprovals;
